/**
 * Firestore read/write for private 1:1 chat.
 * Rules: firestore.rules → match /privateChats/{conversationId}/messages/{messageId}
 */
import {
  collection,
  addDoc,
  doc,
  getDoc,
  query,
  limit,
  onSnapshot,
  serverTimestamp,
  getDocs,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  PRIVATE_CHATS_COLLECTION,
  MESSAGES_SUBCOLLECTION,
  FRIENDSHIPS_COLLECTION,
  MAX_MESSAGE_LENGTH,
  PRIVATE_MESSAGE_QUERY_LIMIT,
  PrivateChatErrorCode,
} from './constants';
import { validateMessage } from './validateMessage';
import { formatChatTime } from './formatTime';
import { friendshipDocumentId } from './conversationId';
import { shouldHideChatSender, isAllowedChatSender } from '../utils/legacyMockUsers';

function getPrivateMessagesRef(conversationId) {
  return collection(db, PRIVATE_CHATS_COLLECTION, conversationId, MESSAGES_SUBCOLLECTION);
}

function mapPrivateMessageDoc(d, currentUserId) {
  const data = d.data();
  const fromUserId = data.fromUserId ?? '';
  const createdAt = data.createdAt;
  const sortKey =
    createdAt?.toMillis?.() ?? (createdAt?.seconds ?? 0) * 1000 ?? 0;
  return {
    id: d.id,
    fromUserId,
    fromUserName: data.fromUserName ?? 'Unknown',
    text: data.text ?? '',
    fromMe: fromUserId === currentUserId,
    time: formatChatTime(createdAt),
    createdAt,
    sortKey,
  };
}

export function subscribePrivateChat(conversationId, currentUserId, callback, onError) {
  if (!conversationId) {
    callback([]);
    return () => {};
  }

  const col = getPrivateMessagesRef(conversationId);
  const q = query(col, limit(PRIVATE_MESSAGE_QUERY_LIMIT));

  return onSnapshot(
    q,
    (snapshot) => {
      const messages = snapshot.docs
        .map((d) => mapPrivateMessageDoc(d, currentUserId))
        .filter((msg) => !shouldHideChatSender(msg.fromUserId))
        .sort((a, b) => a.sortKey - b.sortKey);
      callback(messages);
    },
    (err) => {
      console.error('[privateChat] subscription error:', err);
      onError?.(err);
      callback([]);
    }
  );
}

export async function deletePrivateConversation(conversationId) {
  if (!conversationId) return;
  const col = getPrivateMessagesRef(conversationId);
  const BATCH_SIZE = 450;
  while (true) {
    const snap = await getDocs(query(col, limit(BATCH_SIZE)));
    if (snap.empty) return;
    const batch = writeBatch(db);
    snap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    if (snap.docs.length < BATCH_SIZE) return;
  }
}

async function assertPrivateMessagingAllowed(conversationId, fromUserId) {
  const snap = await getDoc(doc(db, FRIENDSHIPS_COLLECTION, conversationId));
  if (!snap.exists()) throw new Error(PrivateChatErrorCode.NOT_FRIENDS);
  const data = snap.data();
  const userIds = data.userIds || [];
  if (!userIds.includes(fromUserId)) throw new Error(PrivateChatErrorCode.NOT_FRIENDS);
  const expectedId = friendshipDocumentId(userIds[0], userIds[1]);
  if (expectedId && conversationId !== expectedId) {
    throw new Error(PrivateChatErrorCode.NOT_FRIENDS);
  }
  const blocked = data.blockedBy || {};
  const otherId = userIds.find((id) => id !== fromUserId);
  if (blocked[fromUserId] === true || (otherId && blocked[otherId] === true)) {
    throw new Error(PrivateChatErrorCode.MESSAGING_BLOCKED);
  }
}

export async function sendPrivateMessage({ conversationId, fromUserId, fromUserName, text }) {
  if (!conversationId) throw new Error('conversationId required');
  if (!isAllowedChatSender(fromUserId)) {
    throw new Error('Sign in with a real account to send private messages.');
  }
  const t = (text ?? '').trim().slice(0, MAX_MESSAGE_LENGTH);
  const validation = validateMessage(t);
  if (!validation.valid) throw new Error(validation.error);

  await assertPrivateMessagingAllowed(conversationId, fromUserId);

  const col = getPrivateMessagesRef(conversationId);
  await addDoc(col, {
    fromUserId: fromUserId ?? '',
    fromUserName: fromUserName ?? 'Unknown',
    text: t,
    createdAt: serverTimestamp(),
  });
}
